// Book storage service — owns the library: import EPUBs, list books, delete,
// rename, save/restore position. All I/O goes through Dexie (see db.ts).
//
// Sync model (2026-06-19): every successful import is mirrored to the cloud
// library in the background. The cloud library is the source of truth across
// devices — see autoSyncCloudLibrary() in cloudLibraryService for the
// incoming half of the loop. Failures are silent: a local book never blocks
// on an unreachable cloud, and a cloud-only book downloads on the next
// library load. The user does not see Upload/Get buttons.

import { db } from "./db";
import type { Book, BookBlobRecord, ReadingPosition } from "@/types/book";
import { extractEpubMetadata } from "./epubService";
import { convertPdfToEpub, type PdfImportProgress } from "./pdfImportService";
import { convertTextToEpub } from "./textEpubService";
import {
  addTombstone,
  autoMirrorBookToCloud,
  deleteCloudBookBySha,
  sha256Hex,
  writeCloudPosition,
} from "./cloudLibraryService";

function uuid(): string {
  // crypto.randomUUID is available in all modern browsers we care about.
  // Fallback keeps TS happy in odd environments.
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  return "id-" + Math.random().toString(36).slice(2) + Date.now().toString(36);
}

/** Import a PDF file: convert it to EPUB on the client, then store it like
 *  any other EPUB import. The original PDF is not retained. */
export async function importPdf(
  file: File,
  onProgress?: (p: PdfImportProgress) => void,
): Promise<Book> {
  const lower = file.name.toLowerCase();
  if (!lower.endsWith(".pdf")) {
    throw new Error("That file doesn't look like a PDF.");
  }
  const epubFile = await convertPdfToEpub(file, { onProgress });
  return importEpub(epubFile);
}

/** Convenience router — pick the right importer for whatever file type
 *  the user just dropped/picked. */
export async function importBookFile(
  file: File,
  onProgress?: (p: PdfImportProgress) => void,
): Promise<Book> {
  const lower = file.name.toLowerCase();
  const mime = (file.type || "").toLowerCase();
  if (lower.endsWith(".pdf") || mime === "application/pdf") {
    return importPdf(file, onProgress);
  }
  // Plain text / Markdown — wrap in a minimal EPUB, then import normally.
  if (
    /\.(txt|text|md|markdown)$/i.test(lower) ||
    mime === "text/plain" ||
    mime === "text/markdown"
  ) {
    const epubFile = await convertTextToEpub(file);
    return importEpub(epubFile);
  }
  return importEpub(file);
}

/** Import an EPUB blob into the library. Returns the new Book row. */
export async function importEpub(file: File): Promise<Book> {
  const lower = file.name.toLowerCase();
  if (!lower.endsWith(".epub")) {
    throw new Error(
      "Only EPUB is supported by importEpub(). For PDFs use importPdf() — it converts to EPUB first."
    );
  }

  const meta = await extractEpubMetadata(file);

  // Compute the SHA up-front so the Book carries its identity into the
  // cloud library; this is what delete-from-cloud and dedupe both key on.
  const cloudSha256 = await sha256Hex(file);

  const book: Book = {
    id: uuid(),
    title: meta.title || file.name.replace(/\.epub$/i, ""),
    author: meta.author,
    coverUrl: undefined, // set lazily from blob below
    fileName: file.name,
    fileType: "epub",
    storedAs: "epub",
    importedAt: new Date().toISOString(),
    cloudSha256,
    // dc:language from the OPF, lowercased. Undefined when the EPUB doesn't
    // declare one — the library chip simply doesn't render then.
    language: meta.language?.trim().toLowerCase() || undefined,
    // Where the prose starts, computed once here so the reader never has to
    // probe the spine while epub.js is building a rendition. Undefined is a
    // valid state and means "open the book the old way".
    firstContentHref: meta.firstContentHref,
    // Real text size, for an honest time-remaining estimate. Undefined
    // means the footer shows no estimate rather than a fabricated one.
    wordCount: meta.wordCount,
    charCount: meta.charCount,
  };

  const blobRecord: BookBlobRecord = {
    id: book.id,
    blob: file,
    coverBlob: meta.coverBlob,
  };

  await db.transaction("rw", db.books, db.bookBlobs, async () => {
    await db.books.put(book);
    await db.bookBlobs.put(blobRecord);
  });

  // Mirror to cloud in the background. autoMirrorBookToCloud silently
  // no-ops if the cloud is unreachable, the same SHA is already there, or
  // the user has cloud sync off — so this never blocks or surfaces noise.
  void autoMirrorBookToCloud(file, book);

  return book;
}

export async function listBooks(): Promise<Book[]> {
  return db.books.orderBy("importedAt").reverse().toArray();
}

export async function getBook(id: string): Promise<Book | undefined> {
  return db.books.get(id);
}

export async function getBookBlob(id: string): Promise<Blob | undefined> {
  const rec = await db.bookBlobs.get(id);
  return rec?.blob;
}

export async function getCoverBlob(id: string): Promise<Blob | undefined> {
  const rec = await db.bookBlobs.get(id);
  return rec?.coverBlob;
}

export async function deleteBook(id: string): Promise<void> {
  // Grab the cloud identity BEFORE wiping the row so we can also remove the
  // mirrored copy. Falling back to a fresh SHA from the blob covers older
  // books that were imported before we started persisting cloudSha256.
  const book = await db.books.get(id);
  let sha = book?.cloudSha256;
  if (!sha) {
    const blob = await getBookBlob(id);
    if (blob) {
      try {
        sha = await sha256Hex(blob);
      } catch {
        /* fine — best effort */
      }
    }
  }

  await db.transaction(
    "rw",
    db.books,
    db.bookBlobs,
    db.positions,
    async () => {
      await db.books.delete(id);
      await db.bookBlobs.delete(id);
      await db.positions.where("bookId").equals(id).delete();
    }
  );

  // Mirror the delete to the cloud library so the book stops coming back on
  // the next auto-sync. Also drop a tombstone — if the cloud is unreachable
  // right now, the tombstone keeps auto-sync from resurrecting it later.
  if (sha) {
    addTombstone(sha);
    void deleteCloudBookBySha(sha);
  }
}

export async function renameBook(
  id: string,
  fields: { title?: string; author?: string }
): Promise<void> {
  const book = await db.books.get(id);
  if (!book) return;
  await db.books.put({
    ...book,
    title: fields.title ?? book.title,
    author: fields.author ?? book.author,
  });
}

export async function markOpened(id: string): Promise<void> {
  const book = await db.books.get(id);
  if (!book) return;
  await db.books.put({ ...book, lastOpenedAt: new Date().toISOString() });
}

export async function savePosition(pos: ReadingPosition): Promise<void> {
  // ── NULL-POSITION DEFENCE (2026-10-06) ───────────────────────────────
  //
  // Refuse to write a position that has no CFI. Writing garbage over a
  // good position is never correct, and "no CFI" is reachable: MEASURED
  // 3/3 on 2026-10-06 in a standalone epub.js harness using this app's
  // exact rendition options, a stage with ~zero height at display() time
  // makes rendition.currentLocation() return null outright — no start, no
  // end. At a 40-48px stage it returns a start and then goes null after
  // the resize. The full-screen/caret work changes the stage box at boot,
  // so this is more reachable now than it was this morning, not less.
  //
  // WHY THE EXISTING `??` PROTECTION IS NOT ENOUGH. Three writers below
  // are already safe and one is not:
  //   - db.books.put uses `pos.cfi ?? book.currentCfi` — safe.
  //   - the localStorage durable mirror is gated on `pos.cfi` — safe.
  //   - the cloud write is inside the same `pos.cfi` gate — safe.
  //   - db.positions.put(pos) writes UNCONDITIONALLY — not safe.
  //
  // That last one is the hole, and it is not harmless. The resume reader
  // takes the dexie tier's CFI from the Book row but takes its TIMESTAMP
  // from this positions row, and only when `row.cfi === meta.currentCfi`.
  // A cfi-less write makes that comparison false, so the dexie tier is
  // offered "untimed" and loses the tier sort to any timed candidate —
  // demoting a genuinely-newest local position below a staler cloud or
  // durable one. The CFI survives; the recency does not.
  //
  // Reported through the same /__pvdiag/ channel as the resume telemetry
  // (deliberate 404, lands in nginx's access log with the query string
  // intact) so a refusal on the BOOX is visible without its console.
  if (!pos.cfi) {
    try {
      const q = new URLSearchParams({
        b: String(pos.bookId).slice(0, 12),
        why: "nocfi",
        spine: String(pos.spineIndex ?? "?"),
        href: String(pos.href ?? "?").slice(0, 80),
        pct:
          typeof pos.progressPercent === "number"
            ? pos.progressPercent.toFixed(4)
            : "unknown",
        ua: navigator.userAgent.slice(0, 40),
      });
      void fetch(`/__pvdiag/nopos?${q.toString()}`, {
        method: "GET",
        cache: "no-store",
      }).catch(() => {});
    } catch {
      /* diagnostics never break a read */
    }
    // eslint-disable-next-line no-console
    console.warn(
      `[pagevoice] position NOT saved — no CFI (href=${pos.href ?? "?"})`,
    );
    return;
  }
  // Merge rather than replace. An undefined spineIndex/href/progressPercent
  // on an otherwise-good write must not erase a value we already hold —
  // the same reasoning as db.books.put's `??` chain below, which the
  // relocated handler's "pass undefined and let `??` keep the stored
  // value" comment already assumes is true of BOTH tables. It was only
  // ever true of db.books.
  const prev = await db.positions.where("bookId").equals(pos.bookId).first();
  await db.positions.put({
    ...(prev ?? {}),
    ...pos,
    spineIndex: pos.spineIndex ?? prev?.spineIndex,
    href: pos.href ?? prev?.href,
    progressPercent: pos.progressPercent ?? prev?.progressPercent,
  });
  const book = await db.books.get(pos.bookId);
  if (book) {
    await db.books.put({
      ...book,
      currentCfi: pos.cfi ?? book.currentCfi,
      currentSpineIndex: pos.spineIndex ?? book.currentSpineIndex,
      progressPercent: pos.progressPercent ?? book.progressPercent,
      lastOpenedAt: new Date().toISOString(),
    });
    // Mirror the position to localStorage keyed by the book's cloud SHA so
    // it survives a Dexie wipe (iOS storage eviction, manual PWA reset,
    // re-install). When the same EPUB lands back from cloud sync, we look
    // up by SHA and restore the position automatically.
    if (book.cloudSha256 && pos.cfi) {
      writeLastCfi(book.cloudSha256, {
        cfi: pos.cfi,
        spineIndex: pos.spineIndex,
        progressPercent: pos.progressPercent,
        savedAt: Date.now(),
      });
      // Fire-and-forget the cloud write. The cloud is the only thing that
      // survives a PWA reinstall on iOS — localStorage and IndexedDB are
      // both wiped by removing + re-adding the home-screen icon. Server
      // refuses to overwrite a newer savedAt with an older one, so cross-
      // device races settle on the most recent write automatically.
      void writeCloudPosition(book.cloudSha256, {
        cfi: pos.cfi,
        href: pos.href,
        progressPercent: pos.progressPercent,
      });
    }
  }
}

// ---------- Durable last-position store (localStorage) ----------

const LAST_CFI_KEY = "pv.lastCfi.v1";

interface DurablePosition {
  cfi: string;
  spineIndex?: number;
  progressPercent?: number;
  savedAt: number;
}

function readMap(): Record<string, DurablePosition> {
  try {
    const raw = localStorage.getItem(LAST_CFI_KEY);
    if (!raw) return {};
    const obj = JSON.parse(raw);
    return obj && typeof obj === "object" ? obj : {};
  } catch {
    return {};
  }
}

function writeMap(m: Record<string, DurablePosition>) {
  try {
    localStorage.setItem(LAST_CFI_KEY, JSON.stringify(m));
  } catch {
    /* over quota — silently drop */
  }
}

function writeLastCfi(sha: string, p: DurablePosition) {
  const m = readMap();
  m[sha] = p;
  writeMap(m);
}

/** 2026-10-06 — Flush a position using ONLY synchronous / fire-and-forget
 *  primitives, for use from `pagehide` and `visibilitychange`→hidden.
 *
 *  Android can freeze or kill a backgrounded page with no further task-queue
 *  turns, so anything awaited is a coin flip: a Dexie put that is still
 *  pending when the app is frozen simply never lands, and `unload` does not
 *  fire at all on Android. The two things that DO survive are
 *  `localStorage.setItem` (synchronous, committed before the handler
 *  returns) and `navigator.sendBeacon` (handed to the browser process, which
 *  delivers it after the page is gone).
 *
 *  This does NOT touch Dexie — on purpose. The normal savePosition() path
 *  already keeps Dexie current during reading; this is the last-gasp write,
 *  and the durable localStorage tier is read back on open as a resume
 *  candidate, so a position that only lands here is still honoured. */
export function flushPositionSync(
  sha: string | null | undefined,
  pos: { cfi: string; href?: string; progressPercent?: number },
): void {
  if (!sha || !pos.cfi) return;
  try {
    writeLastCfi(sha, {
      cfi: pos.cfi,
      progressPercent: pos.progressPercent,
      savedAt: Date.now(),
    });
  } catch {
    /* quota — the beacon below is still worth trying */
  }
  // Deliberately NO network write here. sendBeacon() can only issue POST,
  // and the lib server accepts position writes on PUT only (do_PUT, "the
  // only PUT this server accepts") — a beacon would be a silent 404. The
  // localStorage write above is enough: the durable tier is one of the
  // three resume candidates read on open, and it is written with a fresh
  // timestamp, so newest-wins picks it up. Adding POST support server-side
  // is the follow-up if we ever want the last-gasp write to reach the
  // cloud as well.
}

/** Look up a previously-saved reading position by the book's cloud SHA.
 *  Used on book open when Dexie's currentCfi is empty (fresh import after
 *  a wipe) so the reader still resumes where the user left off. */
export function readLastCfiBySha(sha: string): DurablePosition | undefined {
  if (!sha) return undefined;
  return readMap()[sha];
}

export async function getPosition(
  bookId: string
): Promise<ReadingPosition | undefined> {
  return db.positions.where("bookId").equals(bookId).first();
}
